import React, { useEffect, useRef, useState } from 'react';

const CoreValues = () => {
  const [activeSection, setActiveSection] = useState(null);
  const sectionRefs = useRef({});

  const values = [
    {
      id: 'learning',
      title: 'Continuous Learning',
      icon: '📚',
      description:
        'We believe in the power of knowledge and continuous improvement. We are committed to fostering a culture of learning, where innovation and personal growth are encouraged at every level. Our dedication to learning ensures we stay ahead in an ever-evolving world, enabling both our employees and clients to reach their full potential.',
    },
    {
      id: 'trust',
      title: 'Trust & Integrity',
      icon: '🤝',
      description:
        'Trust is the cornerstone of everything we do. We build transparent, respectful relationships based on honesty and integrity, ensuring that our clients, employees, and partners can rely on us for consistent, ethical practices in every interaction.',
    },
    {
      id: 'motivation',
      title: 'Motivation & Empowerment',
      icon: '🚀',
      description:
        'We inspire and empower individuals to achieve their goals through a supportive and motivating environment. By encouraging ownership, setting clear goals, and celebrating progress, we fuel the passion and drive that lead to success for our team and our clients.',
    },
    {
      id: 'excellence',
      title: 'Excellence',
      icon: '🏆',
      description:
        'We are committed to delivering the highest quality in every aspect of our work. We strive for excellence in our services, solutions, and results, ensuring that we exceed expectations and create lasting impact. Our pursuit of excellence drives us to continually innovate and refine our practices to deliver superior value.',
    },
  ];

  const sections = [
    { id: 'overview', title: 'Overview' },
    ...values.map((v, i) => ({ id: v.id, title: `${i + 1}. ${v.title}` })),
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs.current[sectionId]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 bg-white">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-blue-800 mb-2">Core Values</h1>
        <p className="text-xl text-gray-600">IndiaHiring.com</p>
        <p className="text-gray-500 mt-2">Updated: March 18, 2025</p>
      </div>

      {/* Introduction */}
      <div className="bg-blue-50 p-6 rounded-lg shadow-sm mb-8">
        <p className="text-gray-800 leading-relaxed">
          These values are the foundation of IndiaHiring.com, ensuring that we not only
          serve the immediate needs of MSMEs but also contribute to their long-term success.
        </p>
        <p className="text-gray-800 leading-relaxed mt-4 font-medium">
          Explore each value below to see how it shapes our culture and decisions.
        </p>
      </div>

      {/* Table of Contents */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-blue-800 mb-4">Table of Contents</h2>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {sections.slice(1).map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`text-left p-2 rounded-md transition-colors hover:bg-blue-50 ${activeSection === section.id
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-gray-700'
                  }`}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="space-y-10">
        {/* Overview */}
        <div
          ref={(el) => (sectionRefs.current['overview'] = el)}
          id="overview"
          className="scroll-mt-4"
        >
          <h2 className="text-2xl font-bold text-blue-800 mb-4">Overview</h2>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-700">
              Our core values guide how we work, collaborate, and create impact. They’re
              the standards we hold ourselves to as we serve businesses and talent across India.
            </p>
          </div>
        </div>

        {/* Value Sections */}
        {values.map((value, idx) => (
          <div
            key={value.id}
            ref={(el) => (sectionRefs.current[value.id] = el)}
            id={value.id}
            className="scroll-mt-4"
          >
            <h2 className="text-2xl font-bold text-blue-800 mb-4">
              {idx + 1}. {value.title}
            </h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-start gap-3">
                <span className="text-2xl" aria-hidden="true">
                  {value.icon}
                </span>
                <p className="text-gray-700">{value.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Note */}
      <div className="mt-12 bg-blue-50 p-6 rounded-lg shadow-sm">
        <h3 className="text-xl font-bold text-blue-800 mb-3">Why these values matter</h3>
        <p className="text-gray-700">
          They shape our culture, inform our decisions, and define how we partner with MSMEs and talent.
        </p>
      </div>

      {/* Back to Top Button */}
      <div className="fixed bottom-6 right-6">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          aria-label="Back to top"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      </div>

      {/* Copyright */}
      <div className="text-center mt-6">
        <p className="text-sm text-gray-500">© 2025 IndiaHiring.com. All rights reserved.</p>
      </div>
    </div>
  );
};

export default CoreValues;